import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaGoogle, FaUserTie, FaEye, FaEyeSlash } from 'react-icons/fa';
import { BrandLogo } from '../components/BrandLogo';
import { startGoogleAuth } from '../utils/nativeAuth';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showProfessionalLogin, setShowProfessionalLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // If professional login mode, redirect to professional dashboard
      if (showProfessionalLogin) {
        navigate('/professionals/dashboard');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleAuth();
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${showProfessionalLogin ? 'bg-gradient-to-br from-amber-50 to-orange-100' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-4 hover:opacity-80 transition">
            {showProfessionalLogin ? (
              <FaUserTie className="text-amber-600 text-4xl" />
            ) : (
              <BrandLogo className="w-12 h-12" testId="login-logo" />
            )}
            <h1 className="text-3xl font-bold text-gray-800">Legacy Forge Family</h1>
          </Link>
          <p className="text-gray-600">
            {showProfessionalLogin ? 'Professional Advisor Portal' : 'Welcome back to your family'}
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-lg p-8" data-testid="login-form">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {showProfessionalLogin ? 'Professional Login' : 'Log In'}
          </h2>
          
          {showProfessionalLogin && (
            <button
              onClick={() => setShowProfessionalLogin(false)}
              className="mb-4 text-sm text-amber-600 hover:text-amber-800 flex items-center"
              data-testid="back-to-family-login"
            >
              ← Back to Family Login
            </button>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="your@email.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link 
                to="/forgot-password" 
                className="text-sm text-blue-600 hover:text-blue-700"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                showProfessionalLogin 
                  ? 'bg-amber-600 text-white hover:bg-amber-700' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              data-testid="login-submit-btn"
            >
              {loading ? 'Logging in...' : (showProfessionalLogin ? 'Access Dashboard' : 'Log In')}
            </button>
          </form>

          {/* Divider - hide for professional login */}
          {!showProfessionalLogin && (
            <div className="flex items-center my-6">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-4 text-gray-500 text-sm">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
          )}

          {/* Google Login - hide for professional login */}
          {!showProfessionalLogin && (
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center space-x-2 bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold"
              data-testid="google-login-btn"
            >
              <FaGoogle className="text-red-600" />
              <span>Continue with Google</span>
            </button>
          )}

          {/* Sign Up Link */}
          <p className="text-center text-gray-600 mt-6">
            {showProfessionalLogin ? (
              <>
                Not registered yet?{' '}
                <Link to="/pro" className="text-amber-600 hover:text-amber-700 font-medium">
                  Join our network
                </Link>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                  Sign up
                </Link>
              </>
            )}
          </p>
        </div>

        {/* Professional Portal Section - hide when in professional login mode */}
        {!showProfessionalLogin && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mt-4" data-testid="professional-portal-section">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FaUserTie className="text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-amber-900 font-medium text-sm">Are you a Professional Advisor?</p>
                <p className="text-amber-700 text-xs">Access your professional dashboard or join our network</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Link 
                to="/pro" 
                className="flex-1 text-center px-3 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 text-sm font-medium transition-colors"
                data-testid="join-network-link"
              >
                Join Network
              </Link>
              <button
                onClick={() => setShowProfessionalLogin(true)}
                className="flex-1 text-center px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium transition-colors"
                data-testid="professional-login-btn"
              >
                Professional Login
              </button>
            </div>
          </div>
        )}

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-gray-600 hover:text-gray-800">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
